<template>
  <Modal
    v-if="isVisible"
    :header="titulo"
    :visible="isVisible"
    :closable="true"
    :style="{ width: '500px' }"
    data-testid="caixa-modal"
    @update:visible="close"
    @close="close"
  >
    <CaixaForm
      :modo="modo"
      :caixa-aberto="caixaAbertoData"
      :saldo-atual="saldoAtualData"
      :pendentes-conferencia="pendentesConferenciaData"
      :total-conferencia="totalConferenciaData"
      :loading="isSubmitting"
      :cofre-contas="cofreContas"
      @submit="handleSubmit"
      @cancel="close"
    />
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useCaixa } from '../../composables/useCaixa';
import { useFiltro } from '../../composables/useFiltro';
import { useCofre } from '../../composables/useCofre';
import { log } from '../../utils/DebugLogger';
import CaixaForm from './CaixaForm.vue';

const { t } = useI18n();
const filtro = useFiltro();
const {
  caixaAberto,
  saldoAtual,
  registrarMovimentacao,
} = useCaixa();
const { contas: cofreContas, fetchContas } = useCofre();

const isVisible = ref(false);
const modo = ref<'sangria' | 'suprimento' | 'ajuste_positivo' | 'ajuste_negativo' | 'despesa_dinheiro'>('sangria');
const isSubmitting = ref(false);
const caixaAbertoData = ref<any | null>(null);
const saldoAtualData = ref<number>(0);
const pendentesConferenciaData = ref<number>(0);
const totalConferenciaData = ref<number>(0);

// ===========================================
// TÍTULO DO MODAL
// ===========================================

const titulo = computed(() => {
  const map: Record<string, string> = {
    'sangria': t('Registrar Sangria'),
    'suprimento': t('Registrar Suprimento'),
    'ajuste_positivo': t('Ajuste Positivo'),
    'ajuste_negativo': t('Ajuste Negativo'),
    'despesa_dinheiro': t('Despesa em Dinheiro'),
  };
  return map[modo.value] || t('Caixa');
});

// ===========================================
// MODAL LIFECYCLE
// ===========================================

const open = async (data?: { modo: string; caixaAberto?: any; saldoAtual?: number; pendentesConferencia?: number; totalConferencia?: number }) => {
  if (data?.modo) {
    modo.value = data.modo as any;
  }
  if (data?.caixaAberto !== undefined) {
    caixaAbertoData.value = data.caixaAberto;
  }
  if (data?.saldoAtual !== undefined) {
    saldoAtualData.value = data.saldoAtual;
  }
  pendentesConferenciaData.value = data?.pendentesConferencia || 0;
  totalConferenciaData.value = data?.totalConferencia || 0;

  // Carrega contas do Cofre para o select de sangria
  if (modo.value === 'sangria') {
    const rawId = filtro.currentFranchiseId.value || filtro.selectedFranchises.value[0]?.id;
    const franquiaId = Number(rawId);
    if (franquiaId && !isNaN(franquiaId)) {
      try {
        await fetchContas(franquiaId);
      } catch (e: any) {
        log('CaixaModal', 'open', 'Erro ao carregar contas do cofre', e);
      }
    } else {
      log('CaixaModal', 'open', 'Franquia não selecionada, não foi possível carregar cofre');
    }
  }

  isVisible.value = true;
};

const close = () => {
  isVisible.value = false;
};

// ===========================================
// SUBMIT HANDLER
// ===========================================

const handleSubmit = async (formData: Record<string, any>) => {
  isSubmitting.value = true;
  try {
    const franquiaId = Number(filtro.currentFranchiseId.value || filtro.selectedFranchises.value[0]?.id);

    switch (modo.value) {
      case 'sangria': {
        if (!caixaAbertoData.value?.id) throw new Error('Nenhum caixa ativo');
        await registrarMovimentacao({
          caixa_abertura_id: caixaAbertoData.value.id,
          tipo: 'sangria',
          valor: formData.valor,
          entrada_saida: 'S',
          cofre_id: formData.cofre_id,
          observacao: formData.observacao,
        });
        eventBus.emit('caixa.movimentacao-registrada');
        break;
      }

      case 'suprimento': {
        if (!caixaAbertoData.value?.id) throw new Error('Nenhum caixa ativo');
        await registrarMovimentacao({
          caixa_abertura_id: caixaAbertoData.value.id,
          tipo: 'suprimento',
          valor: formData.valor,
          entrada_saida: 'E',
          observacao: formData.observacao,
        });
        eventBus.emit('caixa.movimentacao-registrada');
        break;
      }

      case 'ajuste_positivo': {
        if (!caixaAbertoData.value?.id) throw new Error('Nenhum caixa ativo');
        await registrarMovimentacao({
          caixa_abertura_id: caixaAbertoData.value.id,
          tipo: 'ajuste_positivo',
          valor: formData.valor,
          entrada_saida: 'E',
          observacao: formData.observacao,
        });
        eventBus.emit('caixa.movimentacao-registrada');
        break;
      }

      case 'ajuste_negativo': {
        if (!caixaAbertoData.value?.id) throw new Error('Nenhum caixa ativo');
        await registrarMovimentacao({
          caixa_abertura_id: caixaAbertoData.value.id,
          tipo: 'ajuste_negativo',
          valor: formData.valor,
          entrada_saida: 'S',
          observacao: formData.observacao,
        });
        eventBus.emit('caixa.movimentacao-registrada');
        break;
      }

      case 'despesa_dinheiro': {
        if (!caixaAbertoData.value?.id) throw new Error('Nenhum caixa ativo');
        await registrarMovimentacao({
          caixa_abertura_id: caixaAbertoData.value.id,
          tipo: 'despesa_dinheiro',
          valor: formData.valor,
          entrada_saida: 'S',
          observacao: formData.observacao,
        });
        eventBus.emit('caixa.movimentacao-registrada');
        break;
      }
    }

    close();
  } catch (e: any) {
    log('CaixaModal', 'handleSubmit', 'Erro na operação', e);
    // O erro já está disponível via useCaixa().error
  } finally {
    isSubmitting.value = false;
  }
};

// ===========================================
// EVENT LISTENERS
// ===========================================

eventBus.on('modal.caixa.open', open);

onBeforeUnmount(() => {
  eventBus.off('modal.caixa.open', open);
});
</script>
